import { create } from 'zustand';
import { apiService } from '@/services/api';
import type { StoredConsent, StoredConsentDto } from '@/types';

// Consent status mapping from database enum
const ConsentStatusType = {
  Pending: 0,
  Received: 1,
  Rejected: 2,
  PartiallyAuthorised: 3,
  Valid: 4,
  RevokedByPsu: 5,
  Expired: 6,
  TerminatedByTpp: 7,
} as const;

// Convert database consent to frontend format
function convertConsentDto(dto: StoredConsentDto): StoredConsent | null {
  if (!dto.consentId) return null;

  let status: 'valid' | 'pending' | 'expired' = 'pending';
  if (dto.consentStatus === ConsentStatusType.Valid || dto.consentStatus === ConsentStatusType.PartiallyAuthorised) {
    status = 'valid';
  } else if (dto.consentStatus === ConsentStatusType.Expired || dto.consentStatus === ConsentStatusType.RevokedByPsu || dto.consentStatus === ConsentStatusType.TerminatedByTpp || dto.consentStatus === ConsentStatusType.Rejected) {
    status = 'expired';
  }

  return {
    consentId: dto.consentId,
    aspspCde: dto.aspspCde,
    status,
    createdAt: dto.createdAt,
    expiresAt: dto.validUntil || undefined,
  };
}

interface ConsentStore {
  // State
  consents: StoredConsent[];
  activeConsent: StoredConsent | null;
  isLoading: boolean;
  error: string | null;
  lastFetchedAt: string | null;

  // Actions
  fetchConsents: () => Promise<void>;
  addConsent: (consentId: string, aspspCde: string, status?: 'valid' | 'pending' | 'expired') => void;
  setActiveConsent: (consentId: string) => void;
  getConsentByAspsp: (aspspCde: string) => StoredConsent | undefined;
  getValidConsents: () => StoredConsent[];
  updateConsentStatus: (consentId: string, status: 'valid' | 'pending' | 'expired') => void;
  removeConsent: (consentId: string) => void;
  clearExpiredConsents: () => void;
  hasValidConsent: () => boolean;
  clearError: () => void;
}

export const useConsentStore = create<ConsentStore>()((set, get) => ({
  // Initial state
  consents: [],
  activeConsent: null,
  isLoading: false,
  error: null,
  lastFetchedAt: null,

  // Fetch consents from API
  fetchConsents: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.getMyConsents();

      if (response.value) {
        const consents = response.value
          .map(convertConsentDto)
          .filter((c): c is StoredConsent => c !== null);

        // Set the most recent valid consent as active
        const validConsents = consents.filter((c) => c.status === 'valid');
        const activeConsent = validConsents.length > 0 ? validConsents[0] : null;

        set({
          consents,
          activeConsent,
          isLoading: false,
          lastFetchedAt: new Date().toISOString(),
        });
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch consents',
        isLoading: false,
      });
    }
  },

  // Add a new consent (for immediate use after consent callback)
  addConsent: (consentId: string, aspspCde: string, status: 'valid' | 'pending' | 'expired' = 'valid') => {
    const now = new Date().toISOString();
    // Calculate expiry (we request 180 days, the PSD2 max)
    const expiresAt = new Date(Date.now() + 180 * 24 * 60 * 60 * 1000).toISOString();

    const newConsent: StoredConsent = {
      consentId,
      aspspCde,
      status,
      createdAt: now,
      expiresAt,
    };

    set((state) => {
      // Check if consent already exists
      const existingIndex = state.consents.findIndex((c) => c.consentId === consentId);
      if (existingIndex >= 0) {
        // Update existing consent
        const updatedConsents = [...state.consents];
        updatedConsents[existingIndex] = newConsent;
        return { consents: updatedConsents, activeConsent: newConsent };
      }

      // Add new consent
      return {
        consents: [...state.consents, newConsent],
        activeConsent: newConsent,
      };
    });
  },

  // Set active consent by ID
  setActiveConsent: (consentId: string) => {
    const consent = get().consents.find((c) => c.consentId === consentId);
    if (consent) {
      set({ activeConsent: consent });
    }
  },

  // Get consent by ASPSP code
  getConsentByAspsp: (aspspCde: string) => {
    return get().consents.find(
      (c) => c.aspspCde === aspspCde && c.status === 'valid'
    );
  },

  // Get all valid consents
  getValidConsents: () => {
    const now = new Date().toISOString();
    return get().consents.filter((c) => {
      if (c.status !== 'valid') return false;
      if (c.expiresAt && c.expiresAt < now) return false;
      return true;
    });
  },

  // Update consent status
  updateConsentStatus: (consentId: string, status: 'valid' | 'pending' | 'expired') => {
    set((state) => ({
      consents: state.consents.map((c) =>
        c.consentId === consentId ? { ...c, status } : c
      ),
      activeConsent:
        state.activeConsent?.consentId === consentId
          ? { ...state.activeConsent, status }
          : state.activeConsent,
    }));
  },

  // Remove a consent
  removeConsent: (consentId: string) => {
    set((state) => ({
      consents: state.consents.filter((c) => c.consentId !== consentId),
      activeConsent:
        state.activeConsent?.consentId === consentId ? null : state.activeConsent,
    }));
  },

  // Clear expired consents
  clearExpiredConsents: () => {
    const now = new Date().toISOString();
    set((state) => ({
      consents: state.consents.filter((c) => {
        if (c.expiresAt && c.expiresAt < now) return false;
        if (c.status === 'expired') return false;
        return true;
      }),
    }));
  },

  // Check if there's at least one valid consent
  hasValidConsent: () => {
    return get().getValidConsents().length > 0;
  },

  // Clear error
  clearError: () => set({ error: null }),
}));
